/**
 * 下載圖片（後台幻燈箱的「下載」按鈕共用；v1 page/content 與 v2 hooks/cross 各一顆）
 *
 * ## 為什麼舊寫法按了沒反應
 *
 * 圖片放在 Azure blob（appdiag864.blob.core.windows.net），和後台不同網域，
 * 而那個 storage 帳號沒有設定 CORS（對跨來源的 OPTIONS 回 403 CORS not enabled）：
 *   - XHR / fetch 讀跨來源圖片會被瀏覽器擋掉 —— 舊寫法的 XHR 沒有 onerror，失敗是無聲的
 *   - `<a download>` 對跨來源網址會被瀏覽器忽略
 *
 * ## 作法（依序嘗試，前一步成功就停）
 *
 *   1. 直接 fetch —— data: / blob: / 同網域 / 有開 CORS 的來源（例如圖庫的 Unsplash）
 *      Azure 的網址跳過這步：一定失敗，只會在 console 多一條 CORS 錯誤
 *   2. 後端 GET /api/blob/download —— 只接受自家 container 的網址，以 blob 名稱讀出來回 base64
 *   3. 開新分頁 —— 至少讓使用者看得到圖，可以自己另存
 *
 * ⚠️ `@/api` 用動態 import：這支會被 v2/hooks/cross 引用，靜態 import `@/api` 會拉進
 *    usePermission → router，形成循環依賴（HMR 時會出 TDZ，見 useIntent.js 的註解）。
 */

const EXTENSION = {
    "image/jpeg": ".jpg",
    "image/jpg": ".jpg",
    "image/png": ".png",
    "image/webp": ".webp",
    "image/gif": ".gif",
    "image/svg+xml": ".svg",
};

/** 自家 storage —— 直接 fetch 一定會被 CORS 擋，直接走後端 */
const isAzureBlob = (url) => {
    try {
        return new URL(url, window.location.href).hostname.endsWith(".blob.core.windows.net");
    } catch (e) {
        return false;
    }
};

/**
 * 檔名：指定了 name 就用 name_時間戳；否則沿用網址上的檔名；都沒有就 image_時間戳。
 * 副檔名以實際的 MIME 為準（舊寫法存出來的檔案沒有副檔名）。
 */
const fileName = (url, name, mime) => {
    const ext = EXTENSION[(mime || "").toLowerCase()] ?? "";
    if (name) return `${name}_${Date.now()}${ext}`;
    if (!/^(data|blob):/i.test(url)) {
        const base = decodeURIComponent(url.split(/[?#]/)[0].split("/").pop() || "");
        if (/\.[a-z0-9]{2,5}$/i.test(base)) return base;
        if (base) return `${base}${ext}`;
    }
    return `image_${Date.now()}${ext}`;
};

const save = (blob, filename) => {
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        link.remove();
        URL.revokeObjectURL(href);
    }, 150);
};

/**
 * 下載一張圖片
 * @param url  圖片網址（Azure / data: / blob: / 其他）
 * @param name 存檔名稱（不含副檔名，可省略）
 * @returns 是否真的存成檔案（false 代表退回開新分頁）
 */
export const saveImage = async (url, name = null) => {
    if (!url) return false;

    // 1. 直接讀
    if (!isAzureBlob(url)) {
        try {
            const response = await fetch(url);
            if (response.ok) {
                const blob = await response.blob();
                save(blob, fileName(url, name, blob.type));
                return true;
            }
        } catch (e) {
            // 跨來源且沒開 CORS → 下一步
        }
    }

    // 2. 經後端（只有自家 container 的網址會成功，其餘後端回 404）
    if (/^https?:\/\//i.test(url)) {
        try {
            const { default: http } = await import("@/api");
            const { data } = await http.get(
                "/api/blob/download",
                { url },
                // 讀取中顯示遮罩；失敗不要彈錯誤框 —— 還有第 3 步可以退
                { opts: { builtIn: { loadingMask: true, failure: false } } }
            );
            const payload = data?.data;
            if (data?.code === 200 && payload?.blob) {
                const blob = await (await fetch(payload.blob)).blob();
                save(blob, fileName(url, name, payload.mime || blob.type));
                return true;
            }
        } catch (e) {
            // 後端不可用 → 下一步
        }
    }

    // 3. 開新分頁，讓使用者自己另存
    window.open(url, "_blank", "noopener");
    return false;
};
